import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';

/* ── Ordre canonique pour parser les ranges Firestore (Lun-Ven, etc.) ── */
const DAYS_PARSE = [
  { key: 'lun', short: 'L', label: 'Lundi'    },
  { key: 'mar', short: 'M', label: 'Mardi'    },
  { key: 'mer', short: 'M', label: 'Mercredi' },
  { key: 'jeu', short: 'J', label: 'Jeudi'    },
  { key: 'ven', short: 'V', label: 'Vendredi' },
  { key: 'sam', short: 'S', label: 'Samedi'   },
  { key: 'dim', short: 'D', label: 'Dimanche' },
];

/* ── Ordre d'affichage UI : Sam en premier, Ven en dernier ── */
const DAYS_DISPLAY = [
  { key: 'sam', short: 'S', label: 'Samedi'   },
  { key: 'dim', short: 'D', label: 'Dimanche' },
  { key: 'lun', short: 'L', label: 'Lundi'    },
  { key: 'mar', short: 'M', label: 'Mardi'    },
  { key: 'mer', short: 'M', label: 'Mercredi' },
  { key: 'jeu', short: 'J', label: 'Jeudi'    },
  { key: 'ven', short: 'V', label: 'Vendredi' },
];

/* ── Index du jour actuel dans DAYS_PARSE (0=lun … 6=dim) ── */
const todayKeyIndex = () => {
  const d = new Date().getDay(); // 0=dim, 1=lun … 6=sam
  return d === 0 ? 6 : d - 1;   // → index dans DAYS_PARSE
};

/* ── Parser : "Lun-Ven: 8h-18h\nSam-Dim: 8h-18h\nVen: Fermé" → objet par jour ── */
const parseSchedule = (str) => {
  if (!str) return null;

  // Init tous les jours à fermé
  const result = {};
  DAYS_PARSE.forEach(d => { result[d.key] = { open: false, start: '', end: '' }; });

  str.split('\n').forEach(line => {
    const m = line.match(/^(\w+(?:-\w+)?)\s*:\s*(.+)$/i);
    if (!m) return;

    const closed = /ferm/i.test(m[2]);
    const t      = m[2].match(/(\d+h\d*)\s*[-–]\s*(\d+h\d*)/);
    const [start, end] = t ? [t[1], t[2]] : ['', ''];

    // Normalise : "Lun" → "lun", puis prend les 3 premiers chars
    const parts = m[1].split('-').map(x => x.trim().toLowerCase().slice(0, 3));
    const from  = DAYS_PARSE.findIndex(d => d.key === parts[0]);
    const toIdx = parts[1] ? DAYS_PARSE.findIndex(d => d.key === parts[1]) : from;
    const to    = toIdx === -1 ? from : toIdx;

    if (from === -1) return; // label inconnu

    for (let i = from; i <= to; i++) {
      result[DAYS_PARSE[i].key] = { open: !closed, start, end };
    }
  });

  return result;
};

/* ── Résumé pour aujourd'hui ── */
const todaySummary = (schedule) => {
  if (!schedule) return null;
  const day   = DAYS_PARSE[todayKeyIndex()];
  const entry = schedule[day.key];
  if (!entry) return null;
  return entry.open
    ? { open: true,  text: `Auj. ${entry.start}–${entry.end}` }
    : { open: false, text: "Fermé aujourd'hui" };
};

/* ═══════════════════════════════════════════════════════════
   MODE CHIP — ligne compacte (utilisé dans ShopCard)
   ═══════════════════════════════════════════════════════════ */
export const ScheduleChip = ({ openHours }) => {
  const schedule = parseSchedule(openHours);
  const summary  = todaySummary(schedule);
  if (!summary) return null;

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.3rem',
      fontSize: '0.72rem',
      fontWeight: 600,
      color: summary.open ? 'var(--md-primary)' : 'var(--md-text-disabled)',
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
        background: summary.open ? 'var(--md-primary)' : 'var(--md-text-disabled)',
      }} />
      {summary.text}
    </span>
  );
};

/* ═══════════════════════════════════════════════════════════
   MODE DETAIL — tableau complet avec highlight du jour actuel
   Affichage : Sam → Ven (DAYS_DISPLAY)
   ═══════════════════════════════════════════════════════════ */
export const ScheduleDetail = ({ openHours }) => {
  const [expanded, setExpanded] = useState(false);
  const schedule = parseSchedule(openHours);
  if (!schedule) return null;

  /* Jour actuel */
  const todayKey   = DAYS_PARSE[todayKeyIndex()].key;
  const todayEntry = schedule[todayKey];
  const isOpenNow  = todayEntry?.open;

  return (
    <div style={{ width: '100%' }}>

      {/* ── Ligne "aujourd'hui" toujours visible ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.5rem',
        marginBottom: expanded ? '0.75rem' : 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span style={{
            fontSize: '0.65rem',
            fontWeight: 800,
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
            background: isOpenNow ? 'var(--md-primary)' : 'var(--md-text-disabled)',
            color: '#fff',
            borderRadius: '6px',
            padding: '2px 7px',
            flexShrink: 0,
          }}>
            Auj.
          </span>
          <span style={{
            fontWeight: 700,
            fontSize: '0.88rem',
            color: isOpenNow ? 'var(--md-text)' : 'var(--md-text-secondary)',
          }}>
            {todayEntry?.open
              ? `${todayEntry.start} – ${todayEntry.end}`
              : 'Fermé'}
          </span>
        </div>

        {/* Bouton +/- */}
        <button
          onClick={() => setExpanded(v => !v)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center',
            color: 'var(--md-primary)',
            padding: '2px', fontFamily: 'inherit',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {expanded ? <Minus size={18} /> : <Plus size={18} />}
        </button>
      </div>

      {/* ── Tableau complet dépliable ── */}
      <div style={{
        overflow: 'hidden',
        maxHeight: expanded ? '500px' : '0px',
        transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1)',
      }}>
        <div style={{
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid var(--md-outline)',
        }}>
          {DAYS_DISPLAY.map((day, displayIdx) => {
            const entry   = schedule[day.key];
            const isToday = day.key === todayKey;
            const isOpen  = entry?.open;

            return (
              <div
                key={day.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.55rem 0.9rem',
                  background: isToday
                    ? (isOpen ? 'rgba(0,201,167,.10)' : 'rgba(0,0,0,.04)')
                    : 'var(--md-surface)',
                  borderBottom: displayIdx < DAYS_DISPLAY.length - 1
                    ? '1px solid var(--md-outline)'
                    : 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {isToday && (
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                      background: isOpen ? 'var(--md-primary)' : 'var(--md-text-disabled)',
                    }} />
                  )}
                  <span style={{
                    fontSize: '0.82rem',
                    fontWeight: isToday ? 700 : 500,
                    color: isToday ? 'var(--md-text)' : 'var(--md-text-secondary)',
                    minWidth: '72px',
                  }}>
                    {day.label}
                  </span>
                </div>

                {isOpen ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{
                      fontSize: '0.82rem',
                      fontWeight: isToday ? 700 : 500,
                      color: isToday ? 'var(--md-primary)' : 'var(--md-text)',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {entry.start}
                    </span>
                    <span style={{ fontSize: '0.65rem', color: 'var(--md-text-disabled)', fontWeight: 600 }}>–</span>
                    <span style={{
                      fontSize: '0.82rem',
                      fontWeight: isToday ? 700 : 500,
                      color: isToday ? 'var(--md-primary)' : 'var(--md-text)',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {entry.end}
                    </span>
                  </div>
                ) : (
                  <span style={{
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: 'var(--md-text-disabled)',
                    fontStyle: 'italic',
                  }}>
                    Fermé
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};